/**
 * useShellOutputStream — subscribes to the workspace SSE channel and
 * re-emits `shell-output` events to any registered listener on the page.
 *
 * The SSE channel lives at GET /api/chat/threads/:id/workspace/events.
 * The server emits `shell-output` events with { stream, data, t } payloads
 * as stdout/stderr chunks arrive from execute_command.
 *
 * Usage (mount once at the top level, e.g. AiChat):
 *   useShellOutputBroadcaster(threadId, getToken)
 *
 * Usage (in ScriptToolUI to receive chunks):
 *   const chunks = useShellOutputStream()
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useThreadState } from './AiChatProvider';

export interface ShellChunk {
  stream: 'stdout' | 'stderr';
  data: string;
  t: number;
}

type Listener = (chunk: ShellChunk) => void;
const listeners = new Set<Listener>();

function emitChunk(chunk: ShellChunk) {
  for (const fn of listeners) fn(chunk);
}

// ── Batch progress (batch_screenshot, etc.) ──────────────────────────────────
export interface BatchProgressEvent {
  tool: string;
  index: number;
  total: number;
  label?: string;
  locale?: string;
  status: 'preparing' | 'rendering' | 'done' | 'error';
  savedPath?: string;
  error?: string;
}

type BatchListener = (ev: BatchProgressEvent) => void;
const batchListeners = new Set<BatchListener>();

function emitBatch(ev: BatchProgressEvent) {
  for (const fn of batchListeners) fn(ev);
}

// ── Tool progress (vc render/preview, vfx jobs — live progress + ETA) ────────
export interface ToolProgressEvent {
  toolCallId: string;
  // Mirrors ToolProgressKind in packages/agent-core/src/ai-chat/ai-chat.tool-progress.ts.
  // Only some of these have a card that reads them; the rest are emitted and ignored.
  kind: 'vc-render' | 'vc-preview' | 'vfx-job' | 'subthread-fanout' | 'bg-job';
  status: string;
  phase?: string;
  percent?: number | null;
  etaSec?: number | null;
  label?: string;
  estimated?: boolean;
  stalled?: boolean;
  done: boolean;
  error?: string;
}

type ToolProgressListener = (ev: ToolProgressEvent) => void;
const toolProgressListeners = new Set<ToolProgressListener>();

function emitToolProgress(ev: ToolProgressEvent) {
  for (const fn of toolProgressListeners) fn(ev);
}

// ── Context compaction (summarization) — live status while the model pauses ──
// The server emits a `summary` SSE event when it compacts earlier context,
// either before a turn (`turn-start`) or mid-turn between tool steps
// (`mid-turn`). `start` is sent when compaction begins (the model is paused),
// `done` when the new running summary is ready, `error` if it fell back.
export interface SummaryProgressEvent {
  phase: 'start' | 'done' | 'error';
  scope: 'turn-start' | 'mid-turn';
  step?: number;
  tokens?: number;
  beforeTokens?: number;
  afterTokens?: number;
  chars?: number;
  mode?: string;
  durationMs?: number;
  preview?: string;
}

type SummaryListener = (ev: SummaryProgressEvent) => void;
const summaryListeners = new Set<SummaryListener>();

function emitSummary(ev: SummaryProgressEvent) {
  for (const fn of summaryListeners) fn(ev);
}

// ── Deferred workspace restore — the other thing that stalls a turn silently ──
// A revert with workspace returns before its per-file GCS restore finishes, and
// the next turn blocks on completing it. On a heavy thread that is minutes of
// "thinking" with nothing streamed, which reads as a hang.
export interface WorkspaceRestoreEvent {
  phase: 'start' | 'done' | 'error';
  /** Files being rolled back because their content differs from the snapshot. */
  copy?: number;
  /** Files being removed because the snapshot predates them. */
  delete?: number;
  /** Files in the snapshot as a whole — context, not the size of the change. */
  files?: number;
  /** Index of the message whose turn-start snapshot is being restored. */
  messageIndex?: number;
  /** When that snapshot was captured (ISO). */
  capturedAt?: string;
}

type WorkspaceRestoreListener = (ev: WorkspaceRestoreEvent) => void;
const workspaceRestoreListeners = new Set<WorkspaceRestoreListener>();

function emitWorkspaceRestore(ev: WorkspaceRestoreEvent) {
  for (const fn of workspaceRestoreListeners) fn(ev);
}

// ── Cost signal ──────────────────────────────────────────────────────────────
// The server emits `cost` twice over: with `{ turnIndex }` once the turn's
// telemetry (cacheUsage + summaryUsage) lands in Firestore, so the badge can
// refetch /cost without racing the persist — and with `{ inFlightUsd }` after
// every step of the turn in progress, since the persisted figure can't move
// until the turn ends and a long agentic turn spends real money meanwhile.
interface CostEvent {
  turnIndex?: number;
  inFlightUsd?: number;
}

type CostListener = (ev: CostEvent) => void;
const costListeners = new Set<CostListener>();

function emitCost(ev: CostEvent) {
  for (const fn of costListeners) fn(ev);
}

// ── Live context size — drive the context pill mid-turn ─────────────────────
// The server publishes the provider's own input-token count after each step,
// which is the same number the compaction ceiling is compared against.
interface ContextEvent {
  totalTokens: number;
  ceilingTokens: number;
}

type ContextListener = (ev: ContextEvent) => void;
const contextListeners = new Set<ContextListener>();

function emitContext(ev: ContextEvent) {
  for (const fn of contextListeners) fn(ev);
}

// ── Claude subscription usage — the Mac Claude models' usage chip ───────────
// The server emits `mac-usage` once a Mac turn's share of the plan limits is
// recorded, so the chip refetches after the write rather than racing it.
const macUsageListeners = new Set<() => void>();

// ── Workspace file changes — drive live file-panel refresh ───────────────────
// The server emits `file-changed` on API-side mutations (upload/delete/rename/
// restore/render) and `files-synced` when a sandbox→GCS projection lands
// (shell command / background job wrote files). Either means the workspace
// listing is stale and the file panel should refetch.
type FileEventListener = () => void;
const fileEventListeners = new Set<FileEventListener>();

function emitFileEvent() {
  for (const fn of fileEventListeners) fn();
}

// ── Sandbox boot status — surface Modal cold-boot to the UI ──────────────────
// The server emits `sandbox-status` while the per-thread Modal sandbox boots
// (create + seed volume) on the first command of a turn, so the UI can show a
// "starting sandbox…" chip instead of the first command silently eating boot
// latency. `ready`/`failed` are terminal; the hook auto-clears `ready`.
export interface SandboxStatusEvent {
  state: 'warming' | 'ready' | 'failed';
  elapsedMs?: number;
  error?: string;
}

type SandboxStatusListener = (ev: SandboxStatusEvent) => void;
const sandboxStatusListeners = new Set<SandboxStatusListener>();

function emitSandboxStatus(ev: SandboxStatusEvent) {
  for (const fn of sandboxStatusListeners) fn(ev);
}

/**
 * How long to keep the SSE channel open after a run/tool goes idle, so trailing
 * events (final tool-progress / shell-output frames) still flush and back-to-back
 * runs don't churn the connection.
 */
const IDLE_GRACE_MS = 8_000;

/**
 * Mount once (in AiChat) to open the SSE connection for the current thread.
 *
 * The channel is opened ONLY while a run/tool is active (`active`), plus a short
 * grace window afterwards — not for the whole time a thread is merely selected.
 * An open SSE request is an in-flight request on Cloud Run's request-based
 * billing, so holding it open on an idle thread keeps an 8Gi/2-CPU instance
 * billing continuously (and pins it alive, defeating scale-to-zero). Gating on
 * activity lets idle tabs hold no connection so the instance can scale down.
 *
 * Reconnects automatically when threadId changes or the proxy drops a long
 * connection mid-run.
 */
export function useShellOutputBroadcaster(active: boolean) {
  const { currentThreadId } = useThreadState();
  const { user } = useAuth();
  const esRef = useRef<EventSource | null>(null);

  // Desired open-state: true while active, held for IDLE_GRACE_MS after idle.
  const [shouldOpen, setShouldOpen] = useState(active);
  useEffect(() => {
    if (active) {
      setShouldOpen(true);
      return;
    }
    const t = setTimeout(() => setShouldOpen(false), IDLE_GRACE_MS);
    return () => clearTimeout(t);
  }, [active]);

  useEffect(() => {
    if (!currentThreadId || !shouldOpen) return;

    let es: EventSource;
    let closed = false;

    async function connect() {
      let token: string | null = null;
      if (user && 'getIdToken' in user && typeof (user as any).getIdToken === 'function') {
        try { token = await (user as any).getIdToken(); } catch { /* no token */ }
      }

      if (closed) return;

      const streamApiUrl = (import.meta as any).env?.VITE_API_STREAM_URL ?? '';
      const base = streamApiUrl || '';
      // EventSource doesn't support custom headers, so we pass the token as a query param.
      // The server already accepts Bearer tokens in the Authorization header; for SSE we
      // append it as ?token=... and handle it server-side (or skip auth in dev mode).
      const url = `${base}/api/chat/threads/${currentThreadId}/workspace/events${token ? `?token=${encodeURIComponent(token)}` : ''}`;

      es = new EventSource(url, { withCredentials: !streamApiUrl });
      esRef.current = es;

      es.addEventListener('shell-output', (e: MessageEvent) => {
        try {
          const chunk: ShellChunk = JSON.parse(e.data);
          emitChunk(chunk);
        } catch { /* malformed event */ }
      });

      es.addEventListener('batch-progress', (e: MessageEvent) => {
        try {
          emitBatch(JSON.parse(e.data) as BatchProgressEvent);
        } catch { /* malformed event */ }
      });

      es.addEventListener('tool-progress', (e: MessageEvent) => {
        try {
          emitToolProgress(JSON.parse(e.data) as ToolProgressEvent);
        } catch { /* malformed event */ }
      });

      es.addEventListener('summary', (e: MessageEvent) => {
        try {
          emitSummary(JSON.parse(e.data) as SummaryProgressEvent);
        } catch { /* malformed event */ }
      });

      es.addEventListener('workspace-restore', (e: MessageEvent) => {
        try {
          emitWorkspaceRestore(JSON.parse(e.data) as WorkspaceRestoreEvent);
        } catch { /* malformed event */ }
      });

      es.addEventListener('cost', (e: MessageEvent) => {
        try {
          emitCost(JSON.parse(e.data) as CostEvent);
        } catch {
          emitCost({});
        }
      });

      es.addEventListener('mac-usage', () => {
        for (const fn of macUsageListeners) fn();
      });

      es.addEventListener('context', (e: MessageEvent) => {
        try {
          emitContext(JSON.parse(e.data) as ContextEvent);
        } catch { /* malformed event */ }
      });

      const onFileEvent = () => emitFileEvent();
      es.addEventListener('file-changed', onFileEvent);
      es.addEventListener('files-synced', onFileEvent);

      es.addEventListener('sandbox-status', (e: MessageEvent) => {
        try {
          emitSandboxStatus(JSON.parse(e.data) as SandboxStatusEvent);
        } catch { /* malformed event */ }
      });

      es.onerror = () => {
        es.close();
        // EventSource won't auto-recover a closed connection. A long batch can
        // outlive a proxy idle timeout, so reconnect after a short backoff to
        // keep progress events flowing (guarded against unmount / thread switch).
        if (!closed) {
          setTimeout(() => { if (!closed) connect(); }, 1_500);
        }
      };
    }

    connect();

    return () => {
      closed = true;
      esRef.current?.close();
      esRef.current = null;
    };
  }, [currentThreadId, user, shouldOpen]);
}

/**
 * Returns live shell output chunks received since the component mounted.
 * Clears on unmount.
 */
export function useShellOutputStream(): ShellChunk[] {
  const [chunks, setChunks] = useState<ShellChunk[]>([]);

  useEffect(() => {
    const listener: Listener = (chunk) => {
      setChunks((prev) => [...prev, chunk]);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return chunks;
}

/**
 * Returns the latest batch-progress events received since mount, keyed by
 * item index so re-renders of the same item (preparing → rendering → done/error)
 * collapse into one row. Clears on unmount.
 *
 * Pass a `tool` to only accumulate events from that batch tool (e.g.
 * `batch_render_video`) — otherwise a concurrent batch of a different tool would
 * bleed into the same index-keyed map.
 */
export function useBatchProgressStream(tool?: string): BatchProgressEvent[] {
  const [byIndex, setByIndex] = useState<Map<number, BatchProgressEvent>>(new Map());

  useEffect(() => {
    const listener: BatchListener = (ev) => {
      if (tool && ev.tool !== tool) return;
      setByIndex((prev) => {
        const next = new Map(prev);
        next.set(ev.index, ev);
        return next;
      });
    };
    batchListeners.add(listener);
    return () => {
      batchListeners.delete(listener);
    };
  }, [tool]);

  return Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
}

/**
 * Returns the latest `tool-progress` event for a specific tool call (matched by
 * `toolCallId`), or null until one arrives. Drives the live progress bar + ETA
 * on long-running render / GPU-job tool cards. Resets when the toolCallId
 * changes or on unmount.
 */
export function useToolProgress(toolCallId: string | undefined): ToolProgressEvent | null {
  const [event, setEvent] = useState<ToolProgressEvent | null>(null);

  useEffect(() => {
    if (!toolCallId) return;
    setEvent(null);
    const listener: ToolProgressListener = (ev) => {
      if (ev.toolCallId === toolCallId) setEvent(ev);
    };
    toolProgressListeners.add(listener);
    return () => {
      toolProgressListeners.delete(listener);
    };
  }, [toolCallId]);

  return event;
}

export interface SummaryProgressState {
  /** True while the model is paused compacting earlier context. */
  active: boolean;
  /** Which scope is currently compacting (only meaningful while `active`). */
  scope?: 'turn-start' | 'mid-turn';
  /** The most recent completed compaction in this session, for the chip/modal. */
  last: SummaryProgressEvent | null;
  /** How many compactions have completed since this hook mounted. */
  count: number;
}

/**
 * Tracks context-compaction status for the current thread from the `summary`
 * SSE events. `active` flips on while the model pauses to summarize and off
 * when it finishes; `last` holds the most recent completed compaction so the UI
 * can offer a "view summary" chip. Session-scoped — resets on unmount / reload.
 */
export function useSummaryProgress(): SummaryProgressState {
  const [state, setState] = useState<SummaryProgressState>({ active: false, last: null, count: 0 });

  useEffect(() => {
    const listener: SummaryListener = (ev) => {
      setState((prev) => {
        if (ev.phase === 'start') {
          return { ...prev, active: true, scope: ev.scope };
        }
        if (ev.phase === 'done') {
          return { active: false, scope: undefined, last: ev, count: prev.count + 1 };
        }
        // error / fallback — clear the active state, keep any prior result.
        return { ...prev, active: false, scope: undefined };
      });
    };
    summaryListeners.add(listener);
    return () => {
      summaryListeners.delete(listener);
    };
  }, []);

  return state;
}

/**
 * The deferred workspace restore in flight, or null. The server replays an
 * in-flight `start` when this channel connects, so a restore already under way
 * when the turn began is still shown rather than looking like a hang.
 *
 * Carries how much is moving and which point it's moving to, so the chip can
 * say what is being put back — "Restoring workspace…" alone left no way to tell,
 * and the server only announces a restore that has something to do.
 */
export function useWorkspaceRestoreStatus(): WorkspaceRestoreEvent | null {
  const [restore, setRestore] = useState<WorkspaceRestoreEvent | null>(null);

  useEffect(() => {
    const listener: WorkspaceRestoreListener = (ev) => setRestore(ev.phase === 'start' ? ev : null);
    workspaceRestoreListeners.add(listener);
    return () => {
      workspaceRestoreListeners.delete(listener);
    };
  }, []);

  return restore;
}

/**
 * Live cost signal.
 *
 * `tick` increments when a turn's telemetry has been PERSISTED — use it as an
 * effect dep to refetch /cost the moment it's available instead of racing the
 * server-side persist.  `inFlightUsd` is the running spend of the turn still
 * streaming, which the persisted total cannot include yet; it resets to 0 as
 * soon as that turn's own record lands.
 */
export function useCostSignal(threadId?: string | null): { tick: number; inFlightUsd: number } {
  const [state, setState] = useState({ tick: 0, inFlightUsd: 0 });

  // A turn keeps streaming in the thread you just left; its in-flight amount
  // must not be added to the thread you're now looking at.
  useEffect(() => {
    setState((prev) => (prev.inFlightUsd === 0 ? prev : { ...prev, inFlightUsd: 0 }));
  }, [threadId]);

  useEffect(() => {
    const listener: CostListener = (ev) => {
      setState((prev) => {
        if (typeof ev.inFlightUsd === 'number') return { ...prev, inFlightUsd: ev.inFlightUsd };
        // Only the end-of-turn persist (which carries the turn index) folds the
        // in-flight amount into the stored total. Tool-cost events also fire
        // mid-turn and must not zero it, or the badge would dip and recover.
        const settled = typeof ev.turnIndex === 'number';
        return { tick: prev.tick + 1, inFlightUsd: settled ? 0 : prev.inFlightUsd };
      });
    };
    costListeners.add(listener);
    return () => {
      costListeners.delete(listener);
    };
  }, []);

  return state;
}

/** Bumps each time a Mac turn's subscription usage is recorded. */
export function useMacUsageSignal(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const listener = () => setTick((n) => n + 1);
    macUsageListeners.add(listener);
    return () => {
      macUsageListeners.delete(listener);
    };
  }, []);
  return tick;
}

/**
 * Live context size.
 *
 * Mid-turn the server publishes the real input-token count after every step;
 * between turns there is nothing to publish, so the pill falls back to the
 * persisted figure it fetched. Returns `null` until a turn reports something,
 * which is what lets the caller tell "no live value yet" from "zero tokens".
 *
 * Like the cost signal, this resets across a thread switch: a turn still
 * streaming in the thread you just left would otherwise keep driving the pill
 * for the thread you are now looking at.
 */
export function useContextSignal(threadId?: string | null): { totalTokens: number; ceilingTokens: number } | null {
  const [state, setState] = useState<{ totalTokens: number; ceilingTokens: number } | null>(null);

  useEffect(() => {
    setState(null);
  }, [threadId]);

  useEffect(() => {
    const listener: ContextListener = (ev) => {
      if (typeof ev.totalTokens !== 'number' || !(ev.totalTokens > 0)) return;
      setState({ totalTokens: ev.totalTokens, ceilingTokens: ev.ceilingTokens });
    };
    contextListeners.add(listener);
    return () => {
      contextListeners.delete(listener);
    };
  }, []);

  return state;
}

/**
 * Returns a counter that increments on every workspace file mutation the
 * server broadcasts (`file-changed` / `files-synced`). Use it as an effect dep
 * to refetch the workspace listing so the file panel stays live during a chat
 * turn without manual refresh or polling. Session-scoped; resets on unmount.
 */
export function useWorkspaceFileSignal(): number {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const listener: FileEventListener = () => setTick((n) => n + 1);
    fileEventListeners.add(listener);
    return () => {
      fileEventListeners.delete(listener);
    };
  }, []);

  return tick;
}

/**
 * Tracks the per-thread Modal sandbox boot status from `sandbox-status` SSE
 * events. Returns the current status, or null when idle/warm. `warming` stays
 * until the boot resolves; `ready` auto-clears after a brief confirmation so the
 * chip doesn't linger; `failed` persists until the next boot attempt.
 * Session-scoped — resets on unmount / reload.
 */
export function useSandboxStatus(): SandboxStatusEvent | null {
  const [status, setStatus] = useState<SandboxStatusEvent | null>(null);

  useEffect(() => {
    let clearTimer: ReturnType<typeof setTimeout> | undefined;
    const listener: SandboxStatusListener = (ev) => {
      if (clearTimer) { clearTimeout(clearTimer); clearTimer = undefined; }
      setStatus(ev);
      if (ev.state === 'ready') {
        clearTimer = setTimeout(() => setStatus(null), 2_500);
      }
    };
    sandboxStatusListeners.add(listener);
    return () => {
      sandboxStatusListeners.delete(listener);
      if (clearTimer) clearTimeout(clearTimer);
    };
  }, []);

  return status;
}
